import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/** Cart drawer behavior; dialog lifecycle remains owned by theme-drawer. */
class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;

  get #themeDrawer() {
    return this.closest('theme-drawer');
  }

  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener('grohour:cart-add-confirmed', this.#handleConfirmedAdd);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    if (this.#themeDrawer?.hasAttribute('open')) this.#handleDrawerOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener('grohour:cart-add-confirmed', this.#handleConfirmedAdd);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  #handleConfirmedAdd = (event) => {
    // Use the section HTML returned by the successful add, before the full-cart read finishes.
    if (!this.hasAttribute('auto-open')) return;
    if (event.target instanceof Element && event.target.closest('dialog:modal')) return;
    const sectionId = 'cart-drawer-section';
    const html = event.detail.sections?.[sectionId];
    if (!html) return; // Keep the native update path if Shopify could not render the section.
    event.detail.ready = (async () => {
      try {
        sectionRenderer.abortRender(sectionId);
        await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
        const drawer = document.querySelector('#cart-drawer');
        if (!drawer?.open) return false;
        if (!drawer.isOpen) drawer.open();
        return true;
      } catch (error) {
        console.warn('[cart-drawer] Early render unavailable; using normal cart update.', error);
        return false;
      }
    })();
  };

  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    // Wait for the actual cart response; do not open optimistically on click.
    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());
        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }
        const openAndSettle = () => {
          // Let Horizon apply its cart section update before revealing the drawer.
          requestAnimationFrame(() => {
            if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
            settle();
          });
        };
        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');
    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }
    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

